import { ReactNode } from 'react'
import type { TableNode } from '@table-library/react-table-library/types/table'
import { useTranslation } from 'libs/i18n'
import { ContextMenu } from 'shared/components'
import type { Option } from 'shared/components/ContextMenu/types'
import type { CardTableColumn } from 'styles/table'
import { useRentalDetails } from 'features/rentals/hooks/useRentalDetails'
import type { RentalItem } from 'features/rentals/types/rentals.types'

type UseColumnsProps = {
	menuOptions: Option[]
}

const textClassName = 'block truncate text-[13px] text-muted-foreground'
const numberClassName = 'block text-right text-[13px] tabular-nums text-muted-foreground'
const padding = '12px 16px'

const cell = (render: (rental: RentalItem) => ReactNode) => (node: TableNode) =>
	render(node as unknown as RentalItem)

export const useColumns = ({ menuOptions }: UseColumnsProps): CardTableColumn[] => {
	const { t } = useTranslation()
	const getDetails = useRentalDetails()

	return [
		{
			label: t('Rental package'),
			width: 'minmax(180px, 3fr)',
			padding,
			renderCell: cell((item) => (
				<span className="block truncate text-[13px] font-semibold text-foreground">
					{getDetails(item).name}
				</span>
			)),
			select: true,
			pinLeft: true
		},
		{
			label: t('Area type'),
			width: 'minmax(160px, 2.4fr)',
			padding,
			renderCell: cell((item) => (
				<span className={textClassName}>{getDetails(item).boothType}</span>
			))
		},
		{
			label: t('Period'),
			width: 'minmax(100px, 1.6fr)',
			padding,
			renderCell: cell((item) => <span className={textClassName}>{getDetails(item).period}</span>)
		},
		{
			label: t('Price'),
			width: 'minmax(110px, 1.9fr)',
			padding,
			align: 'right',
			renderCell: cell((item) => <span className={numberClassName}>{getDetails(item).price}</span>)
		},
		{
			label: t('Fee'),
			width: 'minmax(80px, 1.2fr)',
			padding,
			align: 'right',
			renderCell: cell((item) => <span className={numberClassName}>{getDetails(item).fee}</span>)
		},
		{
			label: '',
			width: '48px',
			padding: '12px 8px',
			renderCell: cell((item) => (
				<ContextMenu
					dataId={item.id}
					item={item}
					options={menuOptions}
					label={t('Actions for {{ name }}', { name: getDetails(item).name })}
				/>
			))
		}
	]
}
